import { Guid } from "guid-typescript";
import moment from "moment"
import TimetablePassenger from "@/components/Timetable/models/TimetablePassenger";
import TimetableState, { TimetableStates } from '@/components/Timetable/models/TimetableStates';
import { TimetablePhases } from '@/components/Timetable/models/TimetablePhases';
import DateHelper from '@/util/DateHelper';
import Helpers from '@/util/Helpers'
import IMovitPlace from '../../Booking/models/IMovitPlace';
import Passenger from '@/components/Passenger/models/Passenger'

export class Timetable {
    id: Guid;
    idNumber: number;
    deleted: boolean;
    startDate: Date;
    endDate: Date;
    // treated as getter only on api
    // TODO: array of int, not number
    passengerIds: number[];
    timetablePassengers: TimetablePassenger[];

    unnamedPassengersCount: number;
    costCenterName: string;
    driverNotes: string;

    fromPlace: IMovitPlace;
    toPlace: IMovitPlace;

     // FK: Destination
    // TODO: int, not number
    destinationId: number;
    
    note: string;
    // TODO: int, not number
    state: number;
    // TODO: int, not number
    durationTotalHours: number;
    // TODO: int, not number
    durationMinutes: number;   
    originalTimetableId: Guid;

    createdAt?: Date;
    createdByEmail?: string;
    
    updatedAt?: Date;
    updatedByEmail?: string;

    orderedAt?: Date;
    orderedByEmail?: string;

    // Front-end only, flag if this is new
    creating: boolean;

    createWithAllDestinationPassengers: boolean = false;

    description: string;

    rejectedReason: string;

    isRecurrent: number;

    klass: object = Timetable;

    passengerWithRidesCount?: number;

    // passengersWihtoutTransportPermissions: Passenger[]

    constructor(options: Partial<Timetable>) {
        this.id = options && options.id ? options.id : null;
        this.idNumber = options && options.idNumber ? options.idNumber : undefined;
        this.deleted = options ? options.deleted : false;
        this.startDate = options && options.startDate ? new Date(options.startDate) : new Date();
        this.endDate = options && options.endDate ? new Date(options.endDate) : new Date();

        this.destinationId = options && options.destinationId ? options.destinationId : 0;
        this.passengerIds = options && options.passengerIds ? options.passengerIds : [];
        this.timetablePassengers = options && options.timetablePassengers ? options.timetablePassengers : [];
        this.unnamedPassengersCount = options && options.unnamedPassengersCount ? options.unnamedPassengersCount : 0;
        this.costCenterName = options && options.costCenterName ? options.costCenterName : null;
        this.driverNotes = options && options.driverNotes ? options.driverNotes : null;
        this.fromPlace = options && options.fromPlace ? options.fromPlace : null;
        this.toPlace = options && options.toPlace ? options.toPlace : null;
        
        // TODO: Set to empty string instead of null if not given?
        this.note = options && options.note ? options.note.trim() : null;
        this.description = options && options.description ? options.description.trim() : null;
        this.state = options && options.state ? options.state : TimetableStates.New.id;        
        this.rejectedReason = options && options.rejectedReason ? options.rejectedReason : null;
        // TODO: Handle guid vs. nullable guid
        this.originalTimetableId = options && options.originalTimetableId ? options.originalTimetableId : null;
        this.creating = options ? options.creating : false;

        this.createdAt = options && options.createdAt ? new Date(options.createdAt) : undefined;
        this.createdByEmail = options && options.createdByEmail ? options.createdByEmail : undefined;
        
        this.updatedAt = options && options.updatedAt ? new Date(options.updatedAt) : undefined;
        this.updatedByEmail = options && options.updatedByEmail ? options.updatedByEmail : undefined;

        this.orderedAt = options && options.orderedAt && 
            new Date(options.orderedAt).getFullYear() <= 1900
            ? new Date(options.orderedAt) : null;
        this.orderedByEmail = options && options.orderedByEmail ? options.orderedByEmail : undefined;

        this.isRecurrent = options && options.isRecurrent ? options.isRecurrent : 0;

        // this.passengersWihtoutTransportPermissions = options && options.passengersWihtoutTransportPermissions 
        //     ? options.passengersWihtoutTransportPermissions 
        //     : [];

    }

    split(splitDate): Timetable{
        const newTimetable = new Timetable(this);
        newTimetable.state = TimetableStates.New.id;
        newTimetable.startDateString = splitDate;
        newTimetable.endDateString = this.endDateString;
        newTimetable.costCenterName = this.costCenterName;
        newTimetable.unnamedPassengersCount = this.unnamedPassengersCount;
        newTimetable.driverNotes = this.driverNotes;
        newTimetable.fromPlace = this.fromPlace;
        newTimetable.toPlace = this.toPlace;
        delete newTimetable.id;
        delete newTimetable.idNumber;
        // newTimetable.timetablePassengers = [];
        // newTimetable.passengerIds = [];
        if (this.note){
            // TODO: i18n
            newTimetable.note = this.note + " (copy)";
        }

        // Change endDate of old (this) timetable
        const oldTimetableNewEndMoment = moment(splitDate).subtract(1, "days");        
        this.endDateString = oldTimetableNewEndMoment.format("YYYY-MM-DD");

        return newTimetable;
    }

    get noteString(): string {
        return this.note ? this.note : '';
    }

    get idNumberWithHash(): string {
        return `#${this.idNumber.toString()}`;
    }

    get createdByNameFromEmail(){
        return Helpers.nameFromEmail(this.createdByEmail);
    }

    get hasEnoughTimeBeforeStart(): boolean{
        const momentNow = moment();
        const momentStart = moment(this.startDateString);
        momentStart.subtract(Timetable.lockDaysBeforeStart, "days");
        return momentNow.isBefore(momentStart); 
    }
    get hasEnoughTimeBeforeEnd(): boolean{
        const momentNow = moment();
        const momentEnd = moment(this.endDateString);
        momentEnd.subtract(Timetable.lockDaysBeforeStart, "days");
        return momentNow.isBefore(momentEnd); 
    }

    get isLocked(): boolean {
        if (Timetable.nonLockingStates.includes(this.stateObj)){
            return false;
        }
        if (Timetable.lockingStates.includes(this.stateObj)){
            return true;
        }
        return this.hasExpired;
    }

    get isAccepted(): boolean {
        return this.stateObj === TimetableStates.Accepted;
    }

    get isOrdered(): boolean {
        return this.stateObj === TimetableStates.Ordered;
    }

    get wasOrdered(): boolean {
        return ![
            TimetableStates.New, 
            TimetableStates.AcceptedModified,
            TimetableStates.RejectedModified
        ].includes(this.stateObj);
    }

    get isOrderable(): boolean {
        if (!Timetable.nonLockingStates.includes(this.stateObj)){
            return false;
        }
        return this.hasEnoughTimeBeforeStart;
    }
    get isOrderIsCancelable(): boolean {
        return this.stateObj !== TimetableStates.Accepted;
    }
    get hasExpired(): boolean {
        return !this.hasEnoughTimeBeforeStart;
    }
    get hasStartDatePassed(): boolean {
        const momentNow = moment();
        const momentStart = moment(this.startDateString);
        return momentNow.isSameOrAfter(momentStart); 
    }
    get hasEndDatePassed(): boolean {
        const momentNow = moment();
        const momentEnd = moment(this.endDateString);
        return momentNow.isSameOrAfter(momentEnd); 
    }
    get daysUntilStart(): number {
        const momentNow = moment();
        const momentStart = moment(this.startDateString);
        return momentStart.diff(momentNow, 'days');
    }
    get daysUntilEnd(): number {
        const momentNow = moment();
        const momentEnd = moment(this.endDateString);
        return momentEnd.diff(momentNow, 'days');
    }
    get stateObj(){
        return TimetableStates.States.find(s => s.id === this.state);
    }
    get stateName(){
        return this.stateObj.name;
    }
    get stateNameLowerCase(){
        return this.stateObj.nameLowerCase;
    }
    get currentStateColor(){
        return this.stateObj.color;
    }
    get currentStateAlertClass(){
        return this.stateObj.alertClass;
    }
    get phase(){
        if (!this.hasStartDatePassed){
            if (this.hasEnoughTimeBeforeStart){
                return TimetablePhases.NotStarted;
            } else {
                return TimetablePhases.AlmostStarted;
            }
        } else {
            if (this.hasEndDatePassed){
                return TimetablePhases.Ended;
            } else {
                if (!this.hasEnoughTimeBeforeEnd){
                    return TimetablePhases.AlmostEnded;
                } else {
                    return TimetablePhases.Ongoing;
                }
            }
        }
    }
    get i18nStateAndPhaseKey(){
        return "timetable.state." + this.stateObj.name.toLowerCase() + ".phase." + this.phase.name.toLowerCase();
    }
    get startDateString() {
        const YYYY = this.startDate.getFullYear();
        const MM = (this.startDate.getMonth() + 1).toString().padStart(2, "0");
        const DD = (this.startDate.getDate()).toString().padStart(2, '0');
        return YYYY + "-" + MM + "-" + DD;
    }

    set startDateString(val) {
        const valDate = new Date(val);
        valDate.setHours(0, 0, 0, 0);
        this.startDate = valDate;
    }

    get endDateString() {
        const YYYY = this.endDate.getFullYear();
        const MM = (this.endDate.getMonth() + 1).toString().padStart(2, "0");
        const DD = (this.endDate.getDate()).toString().padStart(2, '0');
        return YYYY + "-" + MM + "-" + DD;
    }

    set endDateString(val) {
        const valDate = new Date(val);
        valDate.setHours(0, 0, 0, 0);
        this.endDate = valDate;
    }

    get isRejected(): boolean {
        return this.state === TimetableStates.Rejected.id;
    }

    addPassengerIds(passengerIds: number[]){
        // console.log("Timetable", "addPassengerIds()", passengerIds);
        passengerIds.forEach(pid => {
                if (!this.timetablePassengers.find(sp => sp.passengerId === pid)){
                    this.timetablePassengers.push(new TimetablePassenger(this.id, pid));
                }
            }
        );
    }

    removePassengerIds(passengerIds: number[]){
        // console.log("Timetable", "removePassengerIds()", passengerIds);
        passengerIds.forEach(pid => {
            const itemIndex = this.timetablePassengers.findIndex(sp => sp.passengerId === pid);
            if (itemIndex > -1){
                this.timetablePassengers.splice(itemIndex, 1);
            }
        });
        // console.log(this.timetablePassengers);
    }

    public static fromApi(apiTimetable: any): Timetable {
        const timetable = new Timetable(apiTimetable);
        timetable.startDate = apiTimetable.startDate 
            ? new Date(apiTimetable.startDate) 
            : new Date();
        timetable.endDate = apiTimetable.endDate 
            ? new Date(apiTimetable.endDate) 
            : new Date();
        return timetable;
    }

    public static toApi(timetable: Timetable): any {
        const dateHelper = new DateHelper();
        const apiTimetable = {
            destinationId: timetable.destinationId,
            endDate: dateHelper.dateYMD(timetable.endDate),
            id: timetable.id,
            note: timetable.note ? timetable.note.trim() : null,
            description: timetable.description ? timetable.description.trim() : null,
            passengerIds: timetable.passengerIds,
            timetablePassengers: timetable.timetablePassengers,
            startDate: dateHelper.dateYMD(timetable.startDate),
            state: timetable.state,
            createWithAllDestinationPassengers: timetable.createWithAllDestinationPassengers,
            unnamedPassengersCount: timetable.unnamedPassengersCount,
            costCenterName: timetable.costCenterName,
            driverNotes: timetable.driverNotes,
            fromPlace: timetable.fromPlace,
            toPlace: timetable.toPlace
        }
        return apiTimetable;
    }

    static nonLockingStates = [
        TimetableStates.Undefined,
        TimetableStates.New
    ]

    static lockingStates = [
        TimetableStates.Ordered,
        TimetableStates.OrderedProcessing,
        TimetableStates.Accepted,
        TimetableStates.AcceptedOld,
        TimetableStates.Rejected,
        TimetableStates.RejectedOld,
    ]

    static lockDaysBeforeStart: number = -1;

    static CopyFrom(timetable: Timetable): Timetable{       
        return new Timetable({
            id: timetable.id,
            idNumber: timetable.idNumber,
            deleted: timetable.deleted,
            startDate: timetable.startDate,
            endDate: timetable.endDate,
            destinationId: timetable.destinationId,
            passengerIds: timetable.passengerIds,
            timetablePassengers: timetable.timetablePassengers,
            note: timetable.note,
            description: timetable.description,
            state: timetable.state,
            originalTimetableId: timetable.originalTimetableId,
            unnamedPassengersCount: timetable.unnamedPassengersCount,
            costCenterName: timetable.costCenterName,
            driverNotes: timetable.driverNotes,
            fromPlace: timetable.fromPlace,
            toPlace: timetable.toPlace,

            createdAt: timetable.createdAt,
            createdByEmail: timetable.createdByEmail,
            
            updatedAt: timetable.updatedAt,
            updatedByEmail: timetable.updatedByEmail,
    
            orderedAt: timetable.orderedAt,
            orderedByEmail: timetable.orderedByEmail,

            creating: timetable.creating,

        })
    }
    
}

export default Timetable;
